import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Search,
  AlertTriangle,
  Clock,
  CheckCircle2,
  MinusCircle,
  Mail,
  Loader2,
  RefreshCw,
  Check,
  Info,
  Plus,
  Pencil,
  Trash2,
  X,
} from 'lucide-react';
import './Certificates.css';
import { fold } from '../../utils/zoek';

function authFetch(url, options = {}) {
  const token = localStorage.getItem('ozel_token');
  const headers = {
    ...(options.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  return fetch(url, { ...options, headers, credentials: 'include' });
}

const MONTHS_NL = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

// 'YYYY-MM-DD' -> { y, m, d } without ever touching new Date(str), which would
// parse as UTC and shift the day backwards in our timezone.
function parseYmd(str) {
  if (typeof str !== 'string') return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(str.trim());
  if (!m) return null;
  return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };
}

function formatNl(str) {
  const p = parseYmd(str);
  if (!p) return '';
  return `${p.d} ${MONTHS_NL[p.m - 1]} ${p.y}`;
}

// Whole days between today and the given date. Both sides are converted to a
// UTC midnight timestamp first so DST never adds or drops a day.
function daysUntil(str) {
  const p = parseYmd(str);
  if (!p) return null;
  const now = new Date();
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const target = Date.UTC(p.y, p.m - 1, p.d);
  return Math.round((target - today) / 86400000);
}

const STATUS_ORDER = { red: 0, yellow: 1, green: 2, grey: 3 };

const FILTERS = [
  { key: 'all', label: 'Alles' },
  { key: 'red', label: 'Verlopen' },
  { key: 'yellow', label: 'Verloopt binnenkort' },
  { key: 'green', label: 'Geldig' },
];

const STATUS_LABEL = {
  red: 'Verlopen',
  yellow: 'Verloopt binnenkort',
  green: 'Geldig',
  grey: 'Geen einddatum',
};

const STATUS_ICON = {
  red: AlertTriangle,
  yellow: Clock,
  green: CheckCircle2,
  grey: MinusCircle,
};

function sortCertificates(list) {
  return [...list].sort((a, b) => {
    const sa = STATUS_ORDER[a.status] ?? 9;
    const sb = STATUS_ORDER[b.status] ?? 9;
    if (sa !== sb) return sa - sb;
    // Binnen dezelfde status: eerst wat het snelst verloopt.
    const da = a.geldigTot || '';
    const db = b.geldigTot || '';
    if (da && db && da !== db) return da < db ? -1 : 1;
    if (da && !db) return -1;
    if (!da && db) return 1;
    return String(a.workerName || '').localeCompare(String(b.workerName || ''), 'nl');
  });
}

export default function Certificates({ onCountsChange }) {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  // per certificaat-id: { sending, sentTo, error }
  const [reminders, setReminders] = useState({});

  // Toevoegen/bewerken vanuit deze tab: medewerkerlijst voor de keuzelijst,
  // form = null (dicht) of { id?, workerId, ...velden }.
  const [workers, setWorkers] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const EMPTY_FORM = {
    workerId: '', naam: '', geldigTot: '', behaaldOp: '', instantie: '', certificaatnummer: '',
  };

  const countsCbRef = useRef(onCountsChange);
  countsCbRef.current = onCountsChange;

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await authFetch('/api/certificates');
      if (!res.ok) throw new Error(`Server gaf status ${res.status}`);
      const data = await res.json();
      setCertificates(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Certificaten konden niet worden geladen.');
      setCertificates([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Medewerkers voor de keuzelijst, alfabetisch op achternaam.
  useEffect(() => {
    authFetch('/api/workers')
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => {
        const arr = Array.isArray(list) ? list : [];
        arr.sort((a, b) =>
          String(a.lastName || '').localeCompare(String(b.lastName || ''), 'nl', { sensitivity: 'base' }) ||
          String(a.firstName || '').localeCompare(String(b.firstName || ''), 'nl', { sensitivity: 'base' })
        );
        setWorkers(arr);
      })
      .catch(() => setWorkers([]));
  }, []);

  async function saveCertificate(e) {
    e.preventDefault();
    if (!form.workerId) { setFormError('Kies een medewerker.'); return; }
    if (!form.naam.trim()) { setFormError('Certificaatnaam is verplicht.'); return; }
    setSaving(true);
    setFormError('');
    try {
      const payload = {
        naam: form.naam.trim(),
        geldigTot: form.geldigTot || null,
        behaaldOp: form.behaaldOp || null,
        instantie: form.instantie.trim() || null,
        certificaatnummer: form.certificaatnummer.trim() || null,
      };
      const res = form.id
        ? await authFetch(`/api/certificates/${form.id}`, {
            method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
          })
        : await authFetch(`/api/workers/${form.workerId}/certificates`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
          });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || `Opslaan mislukt (status ${res.status})`);
      }
      setForm(null);
      await load();
    } catch (err) {
      setFormError(err.message || 'Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  }

  async function removeCertificate(cert) {
    if (!window.confirm(`Certificaat "${cert.naam}" van ${cert.workerName} verwijderen?`)) return;
    try {
      const res = await authFetch(`/api/certificates/${cert.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`Verwijderen mislukt (status ${res.status})`);
      await load();
    } catch (err) {
      setError(err.message || 'Verwijderen mislukt');
    }
  }

  const counts = useMemo(() => {
    let red = 0;
    let yellow = 0;
    for (const c of certificates) {
      if (c.status === 'red') red += 1;
      else if (c.status === 'yellow') yellow += 1;
    }
    return { red, yellow };
  }, [certificates]);

  // Laat de parent de tellers weten (voor een badge in het menu).
  useEffect(() => {
    if (loading) return;
    if (typeof countsCbRef.current === 'function') {
      countsCbRef.current({ red: counts.red, yellow: counts.yellow });
    }
  }, [loading, counts.red, counts.yellow]);

  const rows = useMemo(() => {
    const term = fold(search).trim();
    const filtered = certificates.filter((c) => {
      if (filter !== 'all' && c.status !== filter) return false;
      if (!term) return true;
      return (
        fold(c.workerName).includes(term) ||
        fold(c.naam).includes(term)
      );
    });
    return sortCertificates(filtered);
  }, [certificates, filter, search]);

  async function sendReminder(cert) {
    setReminders((prev) => ({ ...prev, [cert.id]: { sending: true, sentTo: '', error: '' } }));
    try {
      const res = await authFetch(`/api/certificates/${cert.id}/reminder`, { method: 'POST' });
      let data = null;
      try {
        data = await res.json();
      } catch {
        data = null;
      }
      if (!res.ok || !data || data.ok !== true) {
        throw new Error((data && data.error) || `Versturen mislukt (status ${res.status})`);
      }
      setReminders((prev) => ({
        ...prev,
        [cert.id]: { sending: false, sentTo: data.sentTo || '', error: '' },
      }));
      // Houd "laatst herinnerd op" meteen actueel zonder opnieuw te laden.
      const today = new Date();
      const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(
        today.getDate()
      ).padStart(2, '0')}`;
      setCertificates((prev) =>
        prev.map((c) => (c.id === cert.id ? { ...c, lastReminderAt: iso } : c))
      );
    } catch (err) {
      setReminders((prev) => ({
        ...prev,
        [cert.id]: { sending: false, sentTo: '', error: err.message || 'Versturen mislukt' },
      }));
    }
  }

  function toggleCountFilter(key) {
    setFilter((cur) => (cur === key ? 'all' : key));
  }

  return (
    <div className="certs">
      <div className="certs-header">
        <div className="certs-title">
          <h2>Certificaten</h2>
          <p>Alle certificaten van alle medewerkers, met de aflopende bovenaan.</p>
        </div>

        <button
          type="button"
          className="certs-add-btn"
          onClick={() => { setForm({ ...EMPTY_FORM }); setFormError(''); }}
        >
          <Plus size={16} />
          Certificaat toevoegen
        </button>

        <div className="certs-counters">
          <button
            type="button"
            className={`certs-counter certs-counter--red${filter === 'red' ? ' is-active' : ''}`}
            onClick={() => toggleCountFilter('red')}
          >
            <span className="certs-counter-number">{counts.red}</span>
            <span className="certs-counter-label">verlopen</span>
          </button>
          <button
            type="button"
            className={`certs-counter certs-counter--yellow${filter === 'yellow' ? ' is-active' : ''}`}
            onClick={() => toggleCountFilter('yellow')}
          >
            <span className="certs-counter-number">{counts.yellow}</span>
            <span className="certs-counter-label">verloopt binnenkort</span>
          </button>
        </div>
      </div>

      <div className="certs-controls">
        <div className="certs-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`certs-chip${filter === f.key ? ' is-active' : ''}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="certs-search">
          <Search size={16} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Zoek op medewerker of certificaat..."
          />
        </div>
      </div>

      {error && (
        <div className="certs-error">
          <AlertTriangle size={16} />
          <span>{error}</span>
          <button type="button" className="certs-retry" onClick={load}>
            <RefreshCw size={14} />
            Opnieuw proberen
          </button>
        </div>
      )}

      {loading ? (
        <div className="certs-state">
          <Loader2 size={18} className="certs-spin" />
          <span>Certificaten laden...</span>
        </div>
      ) : rows.length === 0 ? (
        <div className="certs-state certs-state--empty">Geen certificaten gevonden</div>
      ) : (
        <div className="certs-table-wrap">
          <table className="certs-table">
            <thead>
              <tr>
                <th>Medewerker</th>
                <th>Certificaat</th>
                <th>Instantie</th>
                <th>Geldig tot</th>
                <th>Status</th>
                <th aria-label="Acties" />
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => {
                const Icon = STATUS_ICON[c.status] || MinusCircle;
                const rem = reminders[c.id] || {};
                const days = c.status === 'yellow' ? daysUntil(c.geldigTot) : null;
                return (
                  <tr key={c.id} className={`certs-row certs-row--${c.status}`}>
                    <td className="certs-worker">{c.workerName || '—'}</td>
                    <td className="certs-name">
                      {c.naam || '—'}
                      {c.certificaatnummer ? (
                        <span className="certs-sub">nr. {c.certificaatnummer}</span>
                      ) : null}
                    </td>
                    <td className="certs-instantie">{c.instantie || <span className="certs-dash">—</span>}</td>
                    <td className="certs-date">
                      {c.geldigTot ? formatNl(c.geldigTot) : <span className="certs-dash">—</span>}
                    </td>
                    <td className="certs-status-cell">
                      <span className={`certs-badge certs-badge--${c.status}`}>
                        <Icon size={13} />
                        {STATUS_LABEL[c.status] || 'Onbekend'}
                      </span>

                      {c.status === 'yellow' && days !== null && (
                        <span className="certs-days">
                          {days > 0 ? `nog ${days} ${days === 1 ? 'dag' : 'dagen'}` : 'verloopt vandaag'}
                        </span>
                      )}

                      {c.status === 'red' && (
                        <span className="certs-note">
                          <Info size={12} />
                          Medewerker krijgt automatisch bericht
                        </span>
                      )}

                      {c.status === 'yellow' && (
                        <>
                          <button
                            type="button"
                            className="certs-remind-btn"
                            onClick={() => sendReminder(c)}
                            disabled={rem.sending}
                          >
                            {rem.sending ? (
                              <>
                                <Loader2 size={13} className="certs-spin" />
                                Versturen...
                              </>
                            ) : (
                              <>
                                <Mail size={13} />
                                Herinnering sturen
                              </>
                            )}
                          </button>

                          {rem.sentTo !== undefined && rem.sentTo ? (
                            <span className="certs-sent">
                              <Check size={12} />
                              Verstuurd naar {rem.sentTo}
                            </span>
                          ) : null}

                          {rem.error ? <span className="certs-fail">{rem.error}</span> : null}
                        </>
                      )}

                      {c.lastReminderAt && !rem.sending ? (
                        <span className="certs-last">laatst herinnerd op {formatNl(c.lastReminderAt)}</span>
                      ) : null}
                    </td>
                    <td className="certs-actions-cell">
                      <button
                        type="button"
                        className="certs-icon-btn"
                        title="Bewerken"
                        onClick={() => {
                          setFormError('');
                          setForm({
                            id: c.id,
                            workerId: c.workerId,
                            naam: c.naam || '',
                            geldigTot: c.geldigTot || '',
                            behaaldOp: c.behaaldOp || '',
                            instantie: c.instantie || '',
                            certificaatnummer: c.certificaatnummer || '',
                          });
                        }}
                      >
                        <Pencil size={13} />
                      </button>
                      <button
                        type="button"
                        className="certs-icon-btn certs-icon-btn--del"
                        title="Verwijderen"
                        onClick={() => removeCertificate(c)}
                      >
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div className="certs-footer">
          {rows.length} van {certificates.length} certificaten
        </div>
      )}

      {form && (
        <div className="certs-modal-backdrop" onMouseDown={() => !saving && setForm(null)}>
          <form className="certs-modal" onMouseDown={(e) => e.stopPropagation()} onSubmit={saveCertificate}>
            <div className="certs-modal-head">
              <span>{form.id ? 'Certificaat bewerken' : 'Certificaat toevoegen'}</span>
              <button type="button" className="certs-modal-x" onClick={() => setForm(null)} disabled={saving}>
                <X size={16} />
              </button>
            </div>

            <div className="certs-modal-grid">
              <label className="certs-field certs-field--wide">
                <span>Medewerker <b>*</b></span>
                <select
                  value={form.workerId}
                  disabled={!!form.id || saving}
                  onChange={(e) => setForm((f) => ({ ...f, workerId: e.target.value }))}
                >
                  <option value="">— Kies medewerker —</option>
                  {workers.map((w) => (
                    <option key={w.id} value={w.id}>{w.name}</option>
                  ))}
                </select>
              </label>

              <label className="certs-field certs-field--wide">
                <span>Certificaatnaam <b>*</b></span>
                <input
                  value={form.naam}
                  autoFocus={!form.id}
                  placeholder="bijv. VCA Basis"
                  onChange={(e) => setForm((f) => ({ ...f, naam: e.target.value }))}
                />
              </label>

              <label className="certs-field">
                <span>Geldig tot</span>
                <input type="date" value={form.geldigTot || ''}
                  onChange={(e) => setForm((f) => ({ ...f, geldigTot: e.target.value }))} />
              </label>
              <label className="certs-field">
                <span>Behaald op</span>
                <input type="date" value={form.behaaldOp || ''}
                  onChange={(e) => setForm((f) => ({ ...f, behaaldOp: e.target.value }))} />
              </label>

              <label className="certs-field">
                <span>Instantie</span>
                <input value={form.instantie} placeholder="bijv. SSVV"
                  onChange={(e) => setForm((f) => ({ ...f, instantie: e.target.value }))} />
              </label>
              <label className="certs-field">
                <span>Certificaatnummer</span>
                <input value={form.certificaatnummer} placeholder="optioneel"
                  onChange={(e) => setForm((f) => ({ ...f, certificaatnummer: e.target.value }))} />
              </label>
            </div>

            <p className="certs-modal-hint">
              Zonder &lsquo;geldig tot&rsquo; krijgt het certificaat geen status en blokkeert het niets.
            </p>

            {formError && <div className="certs-modal-error">{formError}</div>}

            <div className="certs-modal-actions">
              <button type="button" className="certs-modal-cancel" onClick={() => setForm(null)} disabled={saving}>
                Annuleren
              </button>
              <button type="submit" className="certs-add-btn" disabled={saving}>
                {saving ? <Loader2 size={14} className="certs-spin" /> : <Plus size={14} />}
                {form.id ? 'Opslaan' : 'Toevoegen'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
